// ToolsPage.jsx — KFX Journal · Premium Trading Tools
// Redesigned with premium fintech aesthetics:
//   - Elevated card system with subtle gold glow
//   - Custom 56px input system with focus glow states
//   - Animated result metric cards with count-up effect
//   - Sticky live results panel (right column)
//   - Refined sidebar with premium active states
//   - CSS-only micro-animations (no Framer Motion dependency)

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';

/* ═══════════════════════════════════════════════════════════════════
   DESIGN TOKENS & GLOBAL STYLES
═══════════════════════════════════════════════════════════════════ */
const STYLES = `
  @keyframes kfxt-pulse    { 0%,100%{opacity:1;} 50%{opacity:.3;} }
  @keyframes kfxt-fadeUp   { from{opacity:0;transform:translateY(12px);} to{opacity:1;transform:translateY(0);} }
  @keyframes kfxt-slideIn  { from{transform:translateX(-100%);} to{transform:translateX(0);} }
  @keyframes kfxt-countUp  { from{opacity:0;transform:translateY(6px);} to{opacity:1;transform:translateY(0);} }
  @keyframes kfxt-shimmer  { 0%{background-position:200% 0;} 100%{background-position:-200% 0;} }
  @keyframes kfxt-glow     { 0%,100%{box-shadow:0 0 12px rgba(212,168,67,.06);} 50%{box-shadow:0 0 24px rgba(212,168,67,.14);} }
  @keyframes kfxt-dotPulse { 0%,100%{transform:scale(1);opacity:1;} 50%{transform:scale(1.4);opacity:.7;} }

  .kfxt-tab-bar::-webkit-scrollbar { display:none; }
  .kfxt-scroll::-webkit-scrollbar { width:3px; }
  .kfxt-scroll::-webkit-scrollbar-track { background:transparent; }
  .kfxt-scroll::-webkit-scrollbar-thumb { background:rgba(212,168,67,.15); border-radius:2px; }

  /* Premium input */
  .kfxt-input {
    flex:1; background:transparent; border:none; outline:none;
    padding:0 14px; font-size:14px; font-weight:600;
    color:#F7F3EA; font-family:'Inter',monospace; min-width:0;
    height:100%;
  }
  .kfxt-input::placeholder { color:rgba(168,159,140,.35); font-weight:400; }
  .kfxt-input-wrap {
    display:flex; align-items:center;
    height:52px;
    background:rgba(255,255,255,.03);
    border:1px solid rgba(255,255,255,.07);
    border-radius:10px;
    overflow:hidden;
    transition:border-color .18s, box-shadow .18s;
    position:relative;
  }
  .kfxt-input-wrap:hover {
    border-color:rgba(255,255,255,.12);
    background:rgba(255,255,255,.04);
  }
  .kfxt-input-wrap:focus-within {
    border-color:rgba(212,168,67,.45);
    box-shadow:0 0 0 1px rgba(212,168,67,.2), 0 0 20px rgba(212,168,67,.08);
    background:rgba(212,168,67,.03);
  }

  /* Premium select */
  .kfxt-select {
    width:100%; height:52px;
    background:rgba(255,255,255,.03);
    border:1px solid rgba(255,255,255,.07);
    border-radius:10px;
    padding:0 14px;
    font-size:14px; font-weight:600;
    color:#F7F3EA;
    font-family:'Inter',monospace;
    outline:none; cursor:pointer;
    transition:border-color .18s, box-shadow .18s;
    appearance:none;
    background-image:url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='rgba(168,159,140,.4)' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
    background-repeat:no-repeat;
    background-position:calc(100% - 14px) center;
    background-size:12px;
    padding-right:36px;
  }
  .kfxt-select:hover {
    border-color:rgba(255,255,255,.12);
    background-color:rgba(255,255,255,.04);
  }
  .kfxt-select:focus {
    border-color:rgba(212,168,67,.45);
    box-shadow:0 0 0 1px rgba(212,168,67,.2), 0 0 20px rgba(212,168,67,.08);
  }
  .kfxt-select option, .kfxt-select optgroup { background:#141412; color:#F7F3EA; }

  /* Sidebar nav button */
  .kfxt-nav-btn {
    display:flex; align-items:center; gap:10px;
    padding:8px 10px; margin:1px 8px;
    width:calc(100% - 16px);
    background:transparent; border:none; border-radius:8px;
    cursor:pointer; text-align:left;
    color:rgba(245,240,232,.38);
    transition:background .15s, color .15s;
    font-family:'Inter',sans-serif;
    position:relative;
  }
  .kfxt-nav-btn:hover { background:rgba(255,255,255,.04); color:rgba(245,240,232,.65); }
  .kfxt-nav-btn.active {
    background:linear-gradient(90deg,rgba(212,168,67,.13),rgba(212,168,67,.04));
    color:#D4A843;
  }

  /* Metric card */
  .kfxt-metric-card {
    background:rgba(255,255,255,.025);
    border:1px solid rgba(255,255,255,.06);
    border-radius:12px;
    padding:16px;
    transition:border-color .2s, transform .2s;
    animation:kfxt-countUp .25s ease both;
  }
  .kfxt-metric-card:hover { border-color:rgba(255,255,255,.1); transform:translateY(-1px); }

  /* Section card */
  .kfxt-section {
    background:linear-gradient(180deg,rgba(255,255,255,.025),rgba(255,255,255,.012));
    border:1px solid rgba(255,255,255,.06);
    border-radius:14px;
    padding:20px;
    display:flex; flex-direction:column; gap:14px;
  }

  /* Tool panel entrance */
  .kfxt-tool-panel { animation:kfxt-fadeUp .22s ease both; }

  /* Result panel glow when has data */
  .kfxt-result-panel-live { animation:kfxt-glow 4s ease-in-out infinite; }

  /* Progress bar fill */
  .kfxt-bar-fill { transition:width .5s cubic-bezier(.4,0,.2,1); }

  /* Mobile tab bottom bar */
  .kfxt-tab-btn {
    display:flex; flex-direction:column; align-items:center; gap:2px;
    padding:8px 10px 7px; border:none; cursor:pointer; flex-shrink:0;
    background:transparent; color:rgba(245,240,232,.28);
    transition:all .15s; min-width:54px;
    font-family:'Inter',sans-serif;
    border-top:2px solid transparent;
  }
  .kfxt-tab-btn.active {
    background:rgba(212,168,67,.08);
    color:#D4A843;
    border-top-color:#D4A843;
  }

  /* 2-col sub-grids inside section cards */
  .kfxt-grid-2 {
    display:grid;
    grid-template-columns:1fr 1fr;
    gap:12px;
  }
  /* 2-col metric card pairs in result panels */
  .kfxt-metric-grid {
    display:grid;
    grid-template-columns:1fr 1fr;
    gap:10px;
  }

  @media (max-width:639px) {
    /* Section cards */
    .kfxt-section { padding:14px; gap:12px; }

    /* Main tool layout — always single column */
    .kfxt-main-grid { grid-template-columns:1fr !important; gap:14px !important; }

    /* Collapse all 2-col sub-grids inside sections */
    .kfxt-grid-2 { grid-template-columns:1fr; gap:10px; }

    /* Keep metric pairs as 2-col — they're small and read fine side-by-side */
    /* Except when cards are very narrow — collapse at 360px */
  }

  @media (max-width:400px) {
    /* Very small phones: collapse metric pairs too */
    .kfxt-metric-grid { grid-template-columns:1fr; gap:8px; }
    /* Clock row: stack vertically */
    .kfxt-clock-row { flex-direction:column; }
    .kfxt-clock-row > div { flex:unset !important; width:100%; }
  }
`;

/* ═══════════════════════════════════════════════════════════════════
   SHARED PRIMITIVES
═══════════════════════════════════════════════════════════════════ */

function SectionTitle({ icon, children }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:2 }}>
      {icon && (
        <span style={{ width:28, height:28, borderRadius:7, background:'rgba(212,168,67,.1)', border:'1px solid rgba(212,168,67,.18)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, flexShrink:0 }}>
          {icon}
        </span>
      )}
      <span style={{ fontSize:11, fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase', color:'rgba(212,168,67,.65)', fontFamily:"'Inter',sans-serif" }}>
        {children}
      </span>
    </div>
  );
}

function FieldLabel({ children, hint }) {
  return (
    <div style={{ display:'flex', alignItems:'baseline', gap:6, marginBottom:6 }}>
      <label style={{ fontSize:11, fontWeight:600, color:'rgba(168,159,140,.75)', letterSpacing:'.04em', fontFamily:"'Inter',sans-serif", textTransform:'uppercase' }}>
        {children}
      </label>
      {hint && <span style={{ fontSize:10, color:'rgba(46,204,138,.65)', fontFamily:"'Inter',sans-serif" }}>{hint}</span>}
    </div>
  );
}

// Premium input with affix
function PremiumInput({ label, value, onChange, prefix, suffix, hint, placeholder }) {
  return (
    <div>
      <FieldLabel hint={hint}>{label}</FieldLabel>
      <div className="kfxt-input-wrap">
        {prefix && (
          <span style={{ padding:'0 11px', fontSize:13, fontWeight:600, color:'rgba(168,159,140,.45)', borderRight:'1px solid rgba(255,255,255,.06)', flexShrink:0, lineHeight:'52px', fontFamily:"'Inter',monospace" }}>
            {prefix}
          </span>
        )}
        <input
          className="kfxt-input"
          type="text"
          inputMode="decimal"
          value={value}
          placeholder={placeholder || '0.00'}
          onChange={e => {
            const v = e.target.value;
            if (/^-?\d*\.?\d*$/.test(v) || v === '' || v === '-') onChange(v);
          }}
        />
        {suffix && (
          <span style={{ padding:'0 11px', fontSize:13, fontWeight:600, color:'rgba(168,159,140,.45)', borderLeft:'1px solid rgba(255,255,255,.06)', flexShrink:0, lineHeight:'52px', fontFamily:"'Inter',monospace" }}>
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}

// Pair dropdown
const ALL_PAIRS = [
  { pair:'EURUSD', pv:10,   cat:'Majors'  }, { pair:'GBPUSD', pv:10,   cat:'Majors'  },
  { pair:'USDJPY', pv:9.09, cat:'Majors'  }, { pair:'USDCHF', pv:11.2, cat:'Majors'  },
  { pair:'USDCAD', pv:7.46, cat:'Majors'  }, { pair:'AUDUSD', pv:10,   cat:'Majors'  },
  { pair:'NZDUSD', pv:10,   cat:'Majors'  }, { pair:'EURGBP', pv:12.7, cat:'Crosses' },
  { pair:'EURJPY', pv:9.09, cat:'Crosses' }, { pair:'GBPJPY', pv:9.09, cat:'Crosses' },
  { pair:'AUDJPY', pv:9.09, cat:'Crosses' }, { pair:'CADJPY', pv:9.09, cat:'Crosses' },
  { pair:'CHFJPY', pv:9.09, cat:'Crosses' }, { pair:'NZDJPY', pv:9.09, cat:'Crosses' },
  { pair:'GBPCAD', pv:7.46, cat:'Crosses' }, { pair:'EURCAD', pv:7.46, cat:'Crosses' },
  { pair:'XAUUSD', pv:10,   cat:'Metals'  }, { pair:'XAGUSD', pv:50,   cat:'Metals'  },
  { pair:'USDZAR', pv:0.55, cat:'Exotics' }, { pair:'USDMXN', pv:0.52, cat:'Exotics' },
  { pair:'USDTRY', pv:0.31, cat:'Exotics' },
];
const PAIR_PV = Object.fromEntries(ALL_PAIRS.map(p => [p.pair, p.pv]));

function getPipMultiplier(pair) {
  if (pair === 'XAUUSD') return 10;
  if (pair === 'XAGUSD') return 1000;
  if (pair.includes('JPY')) return 100;
  if (ALL_PAIRS.find(p => p.pair === pair)) return 10000;
  return 1;
}

function PremiumSelect({ label, value, onChange, children }) {
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <select className="kfxt-select" value={value} onChange={e => onChange(e.target.value)}>
        {children}
      </select>
    </div>
  );
}

function PairSelect({ value, onChange }) {
  const cats = [...new Set(ALL_PAIRS.map(p => p.cat))];
  return (
    <PremiumSelect label="Instrument" value={value} onChange={onChange}>
      {cats.map(cat => (
        <optgroup key={cat} label={cat}>
          {ALL_PAIRS.filter(p => p.cat === cat).map(p => (
            <option key={p.pair} value={p.pair}>{p.pair}</option>
          ))}
        </optgroup>
      ))}
    </PremiumSelect>
  );
}

// Live badge
function LiveBadge({ label = 'Auto-calculating · updates as you type' }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 12px', background:'rgba(46,204,138,.05)', border:'1px solid rgba(46,204,138,.12)', borderRadius:8 }}>
      <span style={{ width:6, height:6, borderRadius:'50%', background:'#2ECC8A', flexShrink:0, animation:'kfxt-dotPulse 2s infinite' }} />
      <span style={{ fontSize:11, color:'rgba(46,204,138,.7)', fontFamily:"'Inter',sans-serif", letterSpacing:'.02em' }}>{label}</span>
    </div>
  );
}

// Premium result metric card
function MetricCard({ label, value, sub, color, icon, isEmpty }) {
  const key = value; // for animation trigger
  return (
    <div className="kfxt-metric-card" key={key}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
        <span style={{ fontSize:10, fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', color:'rgba(168,159,140,.6)', fontFamily:"'Inter',sans-serif" }}>
          {label}
        </span>
        {icon && <span style={{ fontSize:14, opacity:0.55 }}>{icon}</span>}
      </div>
      <div style={{ fontSize:26, fontWeight:800, color: isEmpty ? 'rgba(168,159,140,.2)' : (color || '#F7F3EA'), fontFamily:"'Inter',monospace", letterSpacing:'-.02em', lineHeight:1, transition:'color .2s' }}>
        {isEmpty ? '—' : value}
      </div>
      {sub && !isEmpty && (
        <div style={{ fontSize:11, color:'rgba(168,159,140,.5)', marginTop:5, fontFamily:"'Inter',sans-serif" }}>{sub}</div>
      )}
    </div>
  );
}

// Empty state for results panel
function ResultsEmptyState({ icon = '📐', message = 'Enter trade setup to preview position sizing' }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:14, padding:'40px 24px', textAlign:'center' }}>
      <div style={{ width:64, height:64, borderRadius:16, background:'rgba(212,168,67,.06)', border:'1px solid rgba(212,168,67,.1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:26 }}>
        {icon}
      </div>
      <div>
        <div style={{ fontSize:13, fontWeight:600, color:'rgba(168,159,140,.5)', fontFamily:"'Inter',sans-serif", lineHeight:1.5 }}>
          {message}
        </div>
      </div>
    </div>
  );
}

// Sticky result panel wrapper
function ResultPanel({ hasData, children, title = 'Live Results' }) {
  return (
    <div
      className={hasData ? 'kfxt-result-panel-live' : ''}
      style={{
        background:'linear-gradient(180deg,rgba(255,255,255,.03),rgba(255,255,255,.015))',
        border:`1px solid ${hasData ? 'rgba(212,168,67,.14)' : 'rgba(255,255,255,.06)'}`,
        borderRadius:16,
        overflow:'hidden',
        transition:'border-color .4s',
        display:'flex', flexDirection:'column',
      }}
    >
      {/* Panel header */}
      <div style={{ padding:'14px 18px', borderBottom:'1px solid rgba(255,255,255,.05)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontSize:11, fontWeight:700, letterSpacing:'.1em', textTransform:'uppercase', color:'rgba(212,168,67,.6)', fontFamily:"'Inter',sans-serif" }}>{title}</span>
        </div>
        {hasData && (
          <div style={{ display:'flex', alignItems:'center', gap:5 }}>
            <span style={{ width:5, height:5, borderRadius:'50%', background:'#2ECC8A', animation:'kfxt-dotPulse 2s infinite' }} />
            <span style={{ fontSize:9, fontWeight:700, letterSpacing:'.08em', color:'rgba(46,204,138,.65)', textTransform:'uppercase', fontFamily:"'Inter',sans-serif" }}>Live</span>
          </div>
        )}
      </div>
      <div style={{ padding:'16px', flex:1 }}>{children}</div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   TOOL 1 — POSITION SIZING
═══════════════════════════════════════════════════════════════════ */
function ToolPositionSizing({ accountSize: prefill, isMobile }) {
  const [acct,  setAcct]  = useState(prefill || '');
  const [risk,  setRisk]  = useState('1');
  const [entry, setEntry] = useState('');
  const [sl,    setSl]    = useState('');
  const [pair,  setPair]  = useState('EURUSD');

  useEffect(() => { if (prefill) setAcct(String(prefill)); }, [prefill]);

  const result = useMemo(() => {
    const a = parseFloat(acct), r = parseFloat(risk), e = parseFloat(entry), s = parseFloat(sl);
    if (!a || !r || !e || !s || e === s) return null;
    const pv   = PAIR_PV[pair] || 10;
    const rAmt = a * (r / 100);
    const pips = Math.abs(e - s) * getPipMultiplier(pair);
    const lot  = rAmt / (pips * pv);
    const nano = lot * 1000;
    const mini  = lot * 10;
    const micro = lot * 100;
    return { rAmt: rAmt.toFixed(2), pips: pips.toFixed(1), lot: lot.toFixed(4), mini: mini.toFixed(3), micro: micro.toFixed(2), nano: Math.round(nano) };
  }, [acct, risk, entry, sl, pair]);

  const cols = isMobile ? '1fr' : '1.1fr 0.9fr';

  return (
    <div className="kfxt-main-grid" style={{ display:'grid', gridTemplateColumns:cols, gap:20, alignItems:'start' }}>

      {/* ── Left: Inputs ── */}
      <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
        <div className="kfxt-section">
          <SectionTitle icon="🏦">Account Settings</SectionTitle>
          <div className="kfxt-grid-2">
            <PremiumInput label="Account Balance" value={acct} onChange={setAcct} prefix="$" hint={prefill ? '↑ Pre-filled' : undefined} placeholder="10000" />
            <PremiumInput label="Risk %" value={risk} onChange={setRisk} suffix="%" placeholder="1.0" />
          </div>
        </div>

        <div className="kfxt-section">
          <SectionTitle icon="⚙️">Trade Setup</SectionTitle>
          <PairSelect value={pair} onChange={setPair} />
          <div className="kfxt-grid-2">
            <PremiumInput label="Entry Price" value={entry} onChange={setEntry} placeholder="1.08500" />
            <PremiumInput label="Stop Loss" value={sl} onChange={setSl} placeholder="1.08320" />
          </div>
        </div>

        <LiveBadge />
      </div>

      {/* ── Right: Results ── */}
      <ResultPanel hasData={!!result} title="Position Output">
        {result ? (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            <div className="kfxt-metric-grid">
              <MetricCard label="Risk Amount" value={`$${result.rAmt}`} color="#E05555" icon="🎯" />
              <MetricCard label="Stop Distance" value={`${result.pips}`} sub="pips" icon="📏" />
            </div>
            <MetricCard label="Lot Size" value={result.lot} sub="standard lots" color="#D4A843" icon="⚖️" />
            <div className="kfxt-metric-grid">
              <MetricCard label="Mini Lot" value={result.mini} sub="0.1 std" />
              <MetricCard label="Micro Lot" value={result.micro} sub="0.01 std" />
            </div>
            <MetricCard label="Nano Lots" value={result.nano.toLocaleString()} sub="nano lot equivalent" icon="🔬" />

            {/* Risk breakdown bar */}
            <div style={{ marginTop:4, padding:'12px 14px', background:'rgba(255,255,255,.02)', borderRadius:10, border:'1px solid rgba(255,255,255,.05)' }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                <span style={{ fontSize:10, color:'rgba(168,159,140,.55)', textTransform:'uppercase', letterSpacing:'.06em', fontFamily:"'Inter',sans-serif" }}>Capital at risk</span>
                <span style={{ fontSize:12, fontWeight:700, color:'#E05555', fontFamily:"'Inter',monospace" }}>{risk}%</span>
              </div>
              <div style={{ height:4, background:'rgba(255,255,255,.07)', borderRadius:2, overflow:'hidden' }}>
                <div className="kfxt-bar-fill" style={{ height:'100%', width:`${Math.min(parseFloat(risk) * 10, 100)}%`, background:'linear-gradient(90deg,#D4A843,#E05555)', borderRadius:2 }} />
              </div>
            </div>
          </div>
        ) : (
          <ResultsEmptyState message="Enter account balance, risk %, and trade setup to preview position sizing." />
        )}
      </ResultPanel>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   TOOL 2 — RISK:REWARD CALCULATOR
═══════════════════════════════════════════════════════════════════ */
function ToolRiskReward({ accountSize: prefill, isMobile }) {
  const [acct,  setAcct]  = useState(prefill || '');
  const [risk,  setRisk]  = useState('1');
  const [entry, setEntry] = useState('');
  const [sl,    setSl]    = useState('');
  const [tp,    setTp]    = useState('');
  const [pair,  setPair]  = useState('EURUSD');

  useEffect(() => { if (prefill) setAcct(String(prefill)); }, [prefill]);

  const result = useMemo(() => {
    const a = parseFloat(acct), r = parseFloat(risk), e = parseFloat(entry), s = parseFloat(sl), t = parseFloat(tp);
    if (!e || !s || !t || e === s) return null;
    const mult   = getPipMultiplier(pair);
    const rrPips = Math.abs(e - s) * mult;
    const tpPips = Math.abs(t - e) * mult;
    const rr     = tpPips / rrPips;
    const pv     = PAIR_PV[pair] || 10;
    const lot    = (a && r) ? (a * (r / 100)) / (rrPips * pv) : null;
    const payout = lot ? (tpPips * pv * lot) : null;
    const rColor = rr >= 2 ? '#2ECC8A' : rr >= 1 ? '#D4A843' : '#E05555';
    const grade  = rr >= 3 ? 'Excellent' : rr >= 2 ? 'Good' : rr >= 1 ? 'Acceptable' : 'Poor — revisit setup';
    return { rr: rr.toFixed(2), rrPips: rrPips.toFixed(1), tpPips: tpPips.toFixed(1), lot: lot?.toFixed(4), payout: payout?.toFixed(2), rColor, grade };
  }, [acct, risk, entry, sl, tp, pair]);

  const cols = isMobile ? '1fr' : '1.1fr 0.9fr';

  return (
    <div className="kfxt-main-grid" style={{ display:'grid', gridTemplateColumns:cols, gap:20, alignItems:'start' }}>
      <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
        <div className="kfxt-section">
          <SectionTitle icon="🏦">Account Settings</SectionTitle>
          <div className="kfxt-grid-2">
            <PremiumInput label="Account Balance" value={acct} onChange={setAcct} prefix="$" hint={prefill ? '↑ Pre-filled' : undefined} placeholder="10000" />
            <PremiumInput label="Risk %" value={risk} onChange={setRisk} suffix="%" placeholder="1.0" />
          </div>
        </div>
        <div className="kfxt-section">
          <SectionTitle icon="⚙️">Trade Levels</SectionTitle>
          <PairSelect value={pair} onChange={setPair} />
          <PremiumInput label="Entry Price" value={entry} onChange={setEntry} placeholder="1.08500" />
          <div className="kfxt-grid-2">
            <PremiumInput label="Stop Loss" value={sl} onChange={setSl} placeholder="1.08320" />
            <PremiumInput label="Take Profit" value={tp} onChange={setTp} placeholder="1.08860" />
          </div>
        </div>
        <LiveBadge />
      </div>

      <ResultPanel hasData={!!result} title="R:R Analysis">
        {result ? (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {/* Hero RR display */}
            <div style={{ padding:'20px 16px', background:`${result.rColor}0d`, border:`1px solid ${result.rColor}30`, borderRadius:12, textAlign:'center' }}>
              <div style={{ fontSize:10, letterSpacing:'.1em', textTransform:'uppercase', color:'rgba(168,159,140,.5)', marginBottom:8, fontFamily:"'Inter',sans-serif" }}>Risk : Reward</div>
              <div style={{ fontSize:44, fontWeight:800, color:result.rColor, fontFamily:"'Inter',monospace", letterSpacing:'-.03em', lineHeight:1 }}>1 : {result.rr}</div>
              <div style={{ fontSize:11, marginTop:8, color:result.rColor, opacity:.75, fontFamily:"'Inter',sans-serif" }}>{result.grade}</div>
            </div>
            <div className="kfxt-metric-grid">
              <MetricCard label="Risk Pips" value={`${result.rrPips}`} sub="pips at risk" color="#E05555" />
              <MetricCard label="Reward Pips" value={`${result.tpPips}`} sub="pips to target" color="#2ECC8A" />
            </div>
            {result.lot && <MetricCard label="Position Size" value={result.lot} sub="lots" color="#D4A843" icon="⚖️" />}
            {result.payout && <MetricCard label="Potential Payout" value={`$${result.payout}`} color="#2ECC8A" icon="💰" />}
          </div>
        ) : (
          <ResultsEmptyState icon="🎯" message="Enter entry, stop loss, and take profit to analyze risk:reward." />
        )}
      </ResultPanel>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   TOOL 3 — PROFIT TARGET PLANNER
═══════════════════════════════════════════════════════════════════ */
function ToolProfitTarget({ accountSize: prefill, isMobile }) {
  const [acct,       setAcct]       = useState(prefill || '');
  const [monthly,    setMonthly]    = useState('');
  const [tradesPerM, setTradesPerM] = useState('20');
  const [winRate,    setWinRate]    = useState('55');

  useEffect(() => { if (prefill) setAcct(String(prefill)); }, [prefill]);

  const result = useMemo(() => {
    const a = parseFloat(acct), m = parseFloat(monthly), n = parseFloat(tradesPerM), w = parseFloat(winRate) / 100;
    if (!a || !m || !n || !w || w <= 0 || w >= 1) return null;
    const wins = n * w, losses = n * (1 - w);
    const perWin = m / wins;
    const riskPerTrade = perWin * 0.5;
    const rr   = perWin / riskPerTrade;
    const rPct = (riskPerTrade / a) * 100;
    const pct  = (m / a) * 100;
    return { perWin: perWin.toFixed(2), riskPerTrade: riskPerTrade.toFixed(2), rr: rr.toFixed(2), rPct: rPct.toFixed(2), pct: pct.toFixed(2), wins: wins.toFixed(1), losses: losses.toFixed(1) };
  }, [acct, monthly, tradesPerM, winRate]);

  const cols = isMobile ? '1fr' : '1.1fr 0.9fr';

  return (
    <div className="kfxt-main-grid" style={{ display:'grid', gridTemplateColumns:cols, gap:20, alignItems:'start' }}>
      <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
        <div className="kfxt-section">
          <SectionTitle icon="🏦">Account & Goal</SectionTitle>
          <div className="kfxt-grid-2">
            <PremiumInput label="Account Size" value={acct} onChange={setAcct} prefix="$" hint={prefill ? '↑ Pre-filled' : undefined} placeholder="10000" />
            <PremiumInput label="Monthly Target" value={monthly} onChange={setMonthly} prefix="$" placeholder="500" />
          </div>
        </div>
        <div className="kfxt-section">
          <SectionTitle icon="📊">Trading Profile</SectionTitle>
          <div className="kfxt-grid-2">
            <PremiumInput label="Trades / Month" value={tradesPerM} onChange={setTradesPerM} placeholder="20" />
            <PremiumInput label="Win Rate" value={winRate} onChange={setWinRate} suffix="%" placeholder="55" />
          </div>
        </div>
        <LiveBadge />
      </div>

      <ResultPanel hasData={!!result} title="What You Need">
        {result ? (
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            <MetricCard label="Monthly Return" value={`${result.pct}%`} color="#D4A843" icon="🏆" />
            <div className="kfxt-metric-grid">
              <MetricCard label="Per Winning Trade" value={`$${result.perWin}`} color="#2ECC8A" />
              <MetricCard label="Max Risk / Trade" value={`$${result.riskPerTrade}`} color="#E05555" />
            </div>
            <MetricCard label="Risk % Per Trade" value={`${result.rPct}%`} sub={`Min R:R needed: ${result.rr}`} icon="⚠️" />
            <div className="kfxt-metric-grid">
              <MetricCard label="Exp. Wins" value={result.wins} color="#2ECC8A" />
              <MetricCard label="Exp. Losses" value={result.losses} color="#E05555" />
            </div>
          </div>
        ) : (
          <ResultsEmptyState icon="🏆" message="Enter account size, monthly target, and trading stats to reverse-engineer your goal." />
        )}
      </ResultPanel>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   TOOL 4 — DRAWDOWN RECOVERY
═══════════════════════════════════════════════════════════════════ */
function DrawdownMiniChart({ result, peak, dd }) {
  const W = 400, H = 150, PAD = { top:12, right:8, bottom:22, left:36 };
  const iW = W - PAD.left - PAD.right, iH = H - PAD.top - PAD.bottom;
  const start = peak * (1 - dd / 100), target = peak;
  const pct   = result ? parseFloat(result.recPct) / 100 : 0;
  const medM  = result?.medMonths ? parseFloat(result.medMonths) : 6;
  const months = 12;
  const pts = Array.from({ length: months + 1 }, (_, i) => {
    const prog = Math.min(i / Math.max(medM, 0.1), 1);
    const rec  = start + (target - start) * Math.pow(prog, 0.65) * pct;
    const fd   = (1 - pct) * 0.55;
    const fail = start * (1 - fd * Math.min(i / Math.max(medM * 1.4, 1), 1));
    return { month:i, rec:Math.max(rec, start * 0.3), fail:Math.max(fail, start * 0.15) };
  });
  const minV = Math.min(...pts.map(p => p.fail)) * 0.95, maxV = target * 1.06;
  const toX  = i => PAD.left + (i / months) * iW;
  const toY  = v => PAD.top + iH - ((v - minV) / (maxV - minV)) * iH;
  const recPath  = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${toX(p.month).toFixed(1)},${toY(p.rec).toFixed(1)}`).join(' ');
  const failPath = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${toX(p.month).toFixed(1)},${toY(p.fail).toFixed(1)}`).join(' ');
  const baseY   = toY(minV);
  const recArea  = `${recPath} L${toX(months).toFixed(1)},${baseY} L${toX(0).toFixed(1)},${baseY} Z`;
  const failArea = `${failPath} L${toX(months).toFixed(1)},${baseY} L${toX(0).toFixed(1)},${baseY} Z`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', height:'auto', display:'block' }}>
      <defs>
        <linearGradient id="kfxtGradRec"  x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2ECC8A" stopOpacity=".2"/><stop offset="100%" stopColor="#2ECC8A" stopOpacity=".02"/></linearGradient>
        <linearGradient id="kfxtGradFail" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#E05555" stopOpacity=".15"/><stop offset="100%" stopColor="#E05555" stopOpacity=".01"/></linearGradient>
      </defs>
      <line x1={PAD.left} x2={W-PAD.right} y1={toY(target)} y2={toY(target)} stroke="rgba(212,168,67,.3)" strokeWidth="1" strokeDasharray="4,3"/>
      <line x1={PAD.left} x2={W-PAD.right} y1={toY(start)}  y2={toY(start)}  stroke="rgba(255,255,255,.1)" strokeWidth="1" strokeDasharray="3,3"/>
      <path d={failArea} fill="url(#kfxtGradFail)"/>
      <path d={recArea}  fill="url(#kfxtGradRec)"/>
      <path d={failPath} fill="none" stroke="#E05555" strokeWidth="1.5" strokeDasharray="4,3" opacity=".65"/>
      <path d={recPath}  fill="none" stroke="#2ECC8A" strokeWidth="2" opacity=".9"/>
      <circle cx={toX(months)} cy={toY(pts[months].rec)}  r="3" fill="#2ECC8A" opacity=".9"/>
      <circle cx={toX(months)} cy={toY(pts[months].fail)} r="2.5" fill="#E05555" opacity=".7"/>
      {[0, 3, 6, 9, 12].map(i => (
        <text key={i} x={toX(i)} y={H - 4} textAnchor="middle" fill="rgba(168,159,140,.3)" fontSize="8" fontFamily="'Inter',monospace">{i === 0 ? 'Now' : `M${i}`}</text>
      ))}
      {[{ v:start, l:`$${(start/1000).toFixed(0)}k` }, { v:target, l:`$${(target/1000).toFixed(0)}k` }].map(t => (
        <text key={t.v} x={PAD.left - 3} y={toY(t.v) + 4} textAnchor="end" fill="rgba(168,159,140,.28)" fontSize="8" fontFamily="'Inter',monospace">{t.l}</text>
      ))}
    </svg>
  );
}

function ToolDrawdown({ trades, isMobile }) {
  const preStats = useMemo(() => {
    if (!trades || !trades.length) return null;
    const wins   = trades.filter(t => t.result > 0);
    const losses = trades.filter(t => t.result < 0);
    const wr     = trades.length ? Math.round((wins.length / trades.length) * 100) : 50;
    const avgWin  = wins.length  ? (wins.reduce((s, t) => s + t.result, 0)  / wins.length).toFixed(2)  : '';
    const avgLoss = losses.length ? (Math.abs(losses.reduce((s, t) => s + t.result, 0) / losses.length)).toFixed(2) : '';
    return { wr:String(wr), avgWin, avgLoss };
  }, [trades]);

  const [dd,      setDd]      = useState('20');
  const [peakAcct,setPeakAcct]= useState('25000');
  const [wr,      setWr]      = useState(preStats?.wr || '50');
  const [avgWin,  setAvgWin]  = useState(preStats?.avgWin || '150');
  const [avgLoss, setAvgLoss] = useState(preStats?.avgLoss || '100');
  const [tpm,     setTpm]     = useState('20');
  const [ddLimit, setDdLimit] = useState('10');
  const [dailyLim,setDailyLim]= useState('5');

  useEffect(() => {
    if (preStats?.wr)     setWr(preStats.wr);
    if (preStats?.avgWin)  setAvgWin(preStats.avgWin);
    if (preStats?.avgLoss) setAvgLoss(preStats.avgLoss);
  }, [preStats]);

  const result = useMemo(() => {
    const ddN = parseFloat(dd), peak = parseFloat(peakAcct), w = parseFloat(wr) / 100;
    const aw = parseFloat(avgWin), al = parseFloat(avgLoss), n = parseFloat(tpm);
    if (!ddN || !peak || !w || !aw || !al || !n) return null;
    const ev = (w * aw - (1 - w) * al).toFixed(2);
    if (parseFloat(ev) <= 0) {
      return { recPct:'0', medMonths:null, blowoutRisk:'100', ev, gainNeeded:((ddN / (100 - ddN)) * 100).toFixed(1), tradesToDaily:0, atOverallLimit:true };
    }
    const gainNeeded = ((ddN / (100 - ddN)) * 100).toFixed(1);
    const tradesNeeded = (peak * ddN / 100) / parseFloat(ev);
    const medMonths = (tradesNeeded / n).toFixed(1);
    const simN = 500; let recovered = 0, blowouts = 0;
    const ddLimitN = parseFloat(ddLimit) / 100 || 0.1;
    for (let i = 0; i < simN; i++) {
      let bal = peak * (1 - ddN / 100), done = false;
      for (let j = 0; j < n * 12; j++) {
        bal += Math.random() < w ? aw : -al;
        if (bal >= peak)  { recovered++; done = true; break; }
        if (bal <= peak * (1 - ddLimitN)) { blowouts++; done = true; break; }
      }
    }
    const recPct      = ((recovered / simN) * 100).toFixed(1);
    const blowoutRisk = ((blowouts  / simN) * 100).toFixed(1);
    const tradesToDaily = Math.max(0, Math.floor((peak * (parseFloat(dailyLim) / 100)) / al));
    const atOverallLimit = ddN / 100 >= ddLimitN;
    return { recPct, medMonths, blowoutRisk, ev, gainNeeded, tradesToDaily, atOverallLimit };
  }, [dd, peakAcct, wr, avgWin, avgLoss, tpm, ddLimit, dailyLim]);

  const recColor = !result ? 'rgba(168,159,140,.3)' : parseFloat(result.recPct) >= 70 ? '#2ECC8A' : parseFloat(result.recPct) >= 40 ? '#D4A843' : '#E05555';

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
      <div className="kfxt-main-grid" style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:14 }}>
        <div className="kfxt-section">
          <SectionTitle icon="📉">Drawdown Scenario</SectionTitle>
          <div className="kfxt-grid-2">
            <PremiumInput label="Drawdown %" value={dd} onChange={setDd} suffix="%" placeholder="20" />
            <PremiumInput label="Peak Balance" value={peakAcct} onChange={setPeakAcct} prefix="$" placeholder="25000" />
          </div>
          <div className="kfxt-grid-2">
            <PremiumInput label="Win Rate" value={wr} onChange={setWr} suffix="%" hint={preStats ? '↑ Journal' : undefined} placeholder="50" />
            <PremiumInput label="Trades / Mo" value={tpm} onChange={setTpm} placeholder="20" />
          </div>
          <div className="kfxt-grid-2">
            <PremiumInput label="Avg Win ($)" value={avgWin} onChange={setAvgWin} prefix="$" hint={preStats?.avgWin ? '↑ Journal' : undefined} placeholder="150" />
            <PremiumInput label="Avg Loss ($)" value={avgLoss} onChange={setAvgLoss} prefix="$" hint={preStats?.avgLoss ? '↑ Journal' : undefined} placeholder="100" />
          </div>
          <SectionTitle icon="🏛️">Prop Firm Limits</SectionTitle>
          <div className="kfxt-grid-2">
            <PremiumInput label="Overall DD Limit" value={ddLimit} onChange={setDdLimit} suffix="%" placeholder="10" />
            <PremiumInput label="Daily DD Limit" value={dailyLim} onChange={setDailyLim} suffix="%" placeholder="5" />
          </div>
          <LiveBadge />
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {/* Chart */}
          <div style={{ background:'rgba(255,255,255,.02)', border:'1px solid rgba(255,255,255,.05)', borderRadius:12, padding:'12px 10px 6px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'0 4px 8px', flexWrap:'wrap', gap:6 }}>
              <span style={{ fontSize:10, color:'rgba(168,159,140,.35)', textTransform:'uppercase', letterSpacing:'.08em', fontFamily:"'Inter',sans-serif" }}>Recovery Projection</span>
              <div style={{ display:'flex', gap:12 }}>
                {[['#2ECC8A','Recovered'],['#E05555','Failed']].map(([c,l]) => (
                  <div key={l} style={{ display:'flex', alignItems:'center', gap:4 }}>
                    <div style={{ width:10, height:2, background:c, borderRadius:1 }} />
                    <span style={{ fontSize:9, color:'rgba(168,159,140,.4)', fontFamily:"'Inter',sans-serif" }}>{l}</span>
                  </div>
                ))}
              </div>
            </div>
            <DrawdownMiniChart result={result} peak={parseFloat(peakAcct) || 25000} dd={parseFloat(dd) || 20} />
          </div>

          <MetricCard label="Recovery Probability" value={result ? `${result.recPct}%` : '—'} color={recColor} icon="🔄" isEmpty={!result} />
          <div className="kfxt-metric-grid">
            <MetricCard label="Median Recovery" value={result?.medMonths ? `${result.medMonths} mo` : result ? 'Neg. EV' : '—'} isEmpty={!result} />
            <MetricCard label="Blowout Risk" value={result ? `${result.blowoutRisk}%` : '—'} color={result && parseInt(result.blowoutRisk) > 50 ? '#E05555' : '#D4A843'} isEmpty={!result} />
          </div>
          <div className="kfxt-metric-grid">
            <MetricCard label="EV / Trade" value={result ? `${parseFloat(result?.ev) >= 0 ? '+' : ''}$${result.ev}` : '—'} color={result && parseFloat(result.ev) >= 0 ? '#2ECC8A' : '#E05555'} isEmpty={!result} />
            <MetricCard label="Gain Needed" value={result ? `+${result.gainNeeded}%` : '—'} isEmpty={!result} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   TOOL 5 — PROP FIRM TRACKER
═══════════════════════════════════════════════════════════════════ */
function ToolPropFirm({ accountSize: prefill, isMobile }) {
  const [acct,    setAcct]    = useState(prefill || '25000');
  const [phase,   setPhase]   = useState('Challenge');
  const [target,  setTarget]  = useState('10');
  const [ddMax,   setDdMax]   = useState('10');
  const [ddDaily, setDdDaily] = useState('5');
  const [current, setCurrent] = useState('');
  const [days,    setDays]    = useState('30');
  const [trCount, setTrCount] = useState('30');

  useEffect(() => { if (prefill) setAcct(String(prefill)); }, [prefill]);

  const a   = parseFloat(acct)    || 0;
  const tgt = parseFloat(target)  || 0;
  const dm  = parseFloat(ddMax)   || 0;
  const dd  = parseFloat(ddDaily) || 0;
  const pnl = parseFloat(current) || 0;
  const d   = parseInt(days)      || 1;
  const tr  = parseInt(trCount)   || 1;

  const targetAmt   = a * (tgt / 100);
  const totalBuffer = a * (dm  / 100);
  const dailyLimit  = a * (dd  / 100);
  const drawdown    = pnl < 0 ? Math.abs(pnl) : 0;
  const bufferUsed  = totalBuffer > 0 ? Math.min(drawdown / totalBuffer * 100, 100) : 0;
  const bufferLeft  = Math.max(totalBuffer - drawdown, 0);
  const profitPct   = targetAmt > 0 ? Math.min(Math.max(pnl / targetAmt * 100, 0), 100) : 0;
  const remaining   = targetAmt - pnl;
  const perDay      = d  > 0 ? remaining / d  : 0;
  const perTrade    = tr > 0 ? remaining / tr : 0;

  const passed  = pnl >= targetAmt;
  const failed  = totalBuffer > 0 && drawdown >= totalBuffer;
  const danger  = !failed && bufferUsed >= 80;
  const warning = !failed && !danger && bufferUsed >= 50;

  const ddBarColor = failed ? '#E05555' : danger ? '#E05555' : warning ? '#F59E0B' : '#E05555';
  const statusBg   = passed ? 'rgba(46,204,138,.08)'  : failed ? 'rgba(224,85,85,.12)' : danger ? 'rgba(224,85,85,.07)' : warning ? 'rgba(245,158,11,.07)' : 'rgba(212,168,67,.06)';
  const statusBor  = passed ? 'rgba(46,204,138,.22)'  : failed ? 'rgba(224,85,85,.35)' : danger ? 'rgba(224,85,85,.2)'  : warning ? 'rgba(245,158,11,.2)'   : 'rgba(212,168,67,.14)';
  const statusCol  = passed ? '#2ECC8A' : failed ? '#E05555' : danger ? '#E05555' : warning ? '#F59E0B' : '#D4A843';
  const statusText = passed ? '🎉 Challenge target reached — request payout.'
    : failed ? '💀 Account blown — drawdown limit exceeded.'
    : danger  ? `⚠️ Danger — only $${bufferLeft.toFixed(0)} buffer left. Stop trading.`
    : warning ? `⚡ Caution — ${bufferUsed.toFixed(0)}% of drawdown buffer used.`
    : `Need $${remaining.toFixed(2)} more in ${d} day${d !== 1 ? 's' : ''}.`;

  const cols = isMobile ? '1fr' : '1.1fr 0.9fr';

  return (
    <div className="kfxt-main-grid" style={{ display:'grid', gridTemplateColumns:cols, gap:20, alignItems:'start' }}>
      <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
        <div className="kfxt-section">
          <SectionTitle icon="🏛️">Prop Account</SectionTitle>
          <div className="kfxt-grid-2">
            <PremiumInput label="Account Size" value={acct} onChange={setAcct} prefix="$" hint={prefill ? '↑ Pre-filled' : undefined} placeholder="25000" />
            <PremiumSelect label="Phase" value={phase} onChange={setPhase}>
              {['Challenge','Verification','Funded'].map(p => <option key={p} value={p}>{p}</option>)}
            </PremiumSelect>
          </div>
          <PremiumInput label="Profit Target %" value={target} onChange={setTarget} suffix="%" placeholder="10" />
          <div className="kfxt-grid-2">
            <PremiumInput label="Max DD %" value={ddMax} onChange={setDdMax} suffix="%" placeholder="10" />
            <PremiumInput label="Daily DD %" value={ddDaily} onChange={setDdDaily} suffix="%" placeholder="5" />
          </div>
        </div>
        <div className="kfxt-section">
          <SectionTitle icon="📈">Current Status</SectionTitle>
          <PremiumInput label="Current P&L" value={current} onChange={setCurrent} prefix="$" placeholder="-500 or 800" />
          <div className="kfxt-grid-2">
            <PremiumInput label="Days Remaining" value={days} onChange={setDays} placeholder="30" />
            <PremiumInput label="Trades Left" value={trCount} onChange={setTrCount} placeholder="30" />
          </div>
        </div>
        <LiveBadge />
      </div>

      <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
        {/* Status banner */}
        <div style={{ padding:'12px 16px', background:statusBg, border:`1px solid ${statusBor}`, borderRadius:12, fontSize:13, color:statusCol, lineHeight:1.6, fontWeight:failed ? 700 : 500 }}>
          {statusText}
        </div>

        {/* Progress bars card */}
        <div style={{ background:'rgba(255,255,255,.025)', border:'1px solid rgba(255,255,255,.06)', borderRadius:12, padding:'16px' }}>
          <div style={{ marginBottom:14 }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:7 }}>
              <span style={{ fontSize:10, color:'rgba(168,159,140,.5)', textTransform:'uppercase', letterSpacing:'.07em', fontFamily:"'Inter',sans-serif" }}>Profit Progress</span>
              <span style={{ fontSize:12, fontWeight:700, color: passed ? '#2ECC8A' : '#D4A843', fontFamily:"'Inter',monospace" }}>{profitPct.toFixed(1)}%</span>
            </div>
            <div style={{ height:6, background:'rgba(255,255,255,.07)', borderRadius:3, overflow:'hidden' }}>
              <div className="kfxt-bar-fill" style={{ height:'100%', width:`${profitPct}%`, background: passed ? '#2ECC8A' : 'linear-gradient(90deg,#D4A843,#B8891E)', borderRadius:3 }} />
            </div>
            <div style={{ display:'flex', justifyContent:'space-between', marginTop:5 }}>
              <span style={{ fontSize:10, color: pnl > 0 ? '#2ECC8A' : pnl < 0 ? '#E05555' : 'rgba(168,159,140,.35)', fontFamily:"'Inter',monospace" }}>
                {pnl > 0 ? `+$${pnl.toFixed(0)}` : pnl < 0 ? `-$${Math.abs(pnl).toFixed(0)}` : '$0'}
              </span>
              <span style={{ fontSize:10, color:'rgba(168,159,140,.35)', fontFamily:"'Inter',monospace" }}>${targetAmt.toFixed(0)} target</span>
            </div>
          </div>

          {totalBuffer > 0 && (
            <div style={{ paddingTop:12, borderTop:'1px solid rgba(255,255,255,.05)' }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:7 }}>
                <span style={{ fontSize:10, color:'rgba(168,159,140,.5)', textTransform:'uppercase', letterSpacing:'.07em', fontFamily:"'Inter',sans-serif" }}>DD Buffer Used</span>
                <span style={{ fontSize:12, fontWeight:700, color:ddBarColor, fontFamily:"'Inter',monospace" }}>{bufferUsed.toFixed(1)}%</span>
              </div>
              <div style={{ height:6, background:'rgba(255,255,255,.07)', borderRadius:3, overflow:'hidden' }}>
                <div className="kfxt-bar-fill" style={{ height:'100%', width:`${bufferUsed}%`, background:ddBarColor, borderRadius:3 }} />
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', marginTop:5 }}>
                <span style={{ fontSize:10, color: drawdown > 0 ? '#E05555' : 'rgba(168,159,140,.35)', fontFamily:"'Inter',monospace" }}>
                  {drawdown > 0 ? `-$${drawdown.toFixed(0)}` : '$0 used'}
                </span>
                <span style={{ fontSize:10, fontWeight: danger || failed ? 700 : 400, color: failed ? '#E05555' : 'rgba(168,159,140,.5)', fontFamily:"'Inter',monospace" }}>
                  ${bufferLeft.toFixed(0)} left
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="kfxt-metric-grid">
          <MetricCard label="Profit Target" value={targetAmt > 0 ? `$${targetAmt.toFixed(0)}` : '—'} color="#2ECC8A" isEmpty={!targetAmt} />
          <MetricCard label="Still Needed" value={passed ? '✓ Passed' : `$${remaining.toFixed(0)}`} color={passed ? '#2ECC8A' : undefined} isEmpty={!targetAmt} />
        </div>
        <div className="kfxt-metric-grid">
          <MetricCard label="Per Day" value={failed ? '—' : `$${perDay.toFixed(0)}`} isEmpty={failed} />
          <MetricCard label="Per Trade" value={failed ? '—' : `$${perTrade.toFixed(0)}`} isEmpty={failed} />
        </div>
        <div className="kfxt-metric-grid">
          <MetricCard label="Daily Loss Limit" value={dailyLimit > 0 ? `$${dailyLimit.toFixed(0)}` : '—'} color="#E05555" isEmpty={!dailyLimit} />
          <MetricCard label="Max Total DD" value={totalBuffer > 0 ? `$${totalBuffer.toFixed(0)}` : '—'} color="#E05555" isEmpty={!totalBuffer} />
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   TOOL 6 — SESSION CLOCK
═══════════════════════════════════════════════════════════════════ */
// Killzone definitions — times in EST (fractional hours for :30 entries)
// EST = UTC-5 standard, UTC-4 DST. We detect DST offset from the browser.
const KILLZONES = {
  forex: [
    { name:'Asia',         estOpen:20,   estClose:0,    color:'#4E7FD4', icon:'🌏' },
    { name:'London',       estOpen:2,    estClose:5,    color:'#2ECC8A', icon:'🇬🇧' },
    { name:'New York AM',  estOpen:7,    estClose:10,   color:'#E05555', icon:'🗽' },
    { name:'London Close', estOpen:10,   estClose:12,   color:'#C9A84C', icon:'🔔' },
  ],
  indices: [
    { name:'Asia',         estOpen:20,   estClose:0,    color:'#4E7FD4', icon:'🌏' },
    { name:'London',       estOpen:2,    estClose:5,    color:'#2ECC8A', icon:'🇬🇧' },
    { name:'New York AM',  estOpen:8.5,  estClose:11,   color:'#E05555', icon:'🗽' },
    { name:'New York PM',  estOpen:13.5, estClose:16,   color:'#B06ACF', icon:'🌆' },
  ],
};

function useLocalTimezone() {
  return useMemo(() => {
    try {
      const tzName    = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const now       = new Date();
      const utcOffset = -(now.getTimezoneOffset()) / 60;
      const city      = tzName.split('/').pop().replace(/_/g, ' ');
      const sign      = utcOffset >= 0 ? '+' : '-';
      const absH      = Math.floor(Math.abs(utcOffset));
      const absM      = Math.round((Math.abs(utcOffset) - absH) * 60);
      const offsetStr = absM > 0 ? `UTC${sign}${absH}:${String(absM).padStart(2,'0')}` : `UTC${sign}${absH}`;
      return { tzName, city, utcOffset, offsetStr };
    } catch {
      return { tzName:'UTC', city:'Your Location', utcOffset:0, offsetStr:'UTC+0' };
    }
  }, []);
}

// Detect EST offset from browser (handles DST automatically)
function getEstOffset(now) {
  // EST = UTC-5, EDT = UTC-4. We use Intl to get the actual EST offset at this moment.
  try {
    const estDate = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
    // diff in hours between EST local and UTC
    return (estDate - now) / 3600000; // negative number e.g. -5 or -4
  } catch {
    return -5;
  }
}

function ToolSessionClock({ isMobile }) {
  const [now, setNow]   = useState(() => new Date());
  const [tab, setTab]   = useState('forex');
  const { city, utcOffset, offsetStr } = useLocalTimezone();

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const pad  = n => String(Math.floor(n)).padStart(2, '0');
  const fmtH = h => { const hh = ((h % 24) + 24) % 24; const mm = Math.round((hh - Math.floor(hh)) * 60); return `${pad(hh)}:${String(mm).padStart(2,'0')}`; };

  // Current EST fractional hour
  const estOffset   = getEstOffset(now);
  const utcFracHour = now.getUTCHours() + now.getUTCMinutes() / 60 + now.getUTCSeconds() / 3600;
  const estFracHour = ((utcFracHour + estOffset) % 24 + 24) % 24;

  // local clock display
  const localTotalMin = now.getUTCHours() * 60 + now.getUTCMinutes() + Math.round(utcOffset * 60);
  const localH   = ((Math.floor(localTotalMin / 60)) % 24 + 24) % 24;
  const localMin = ((localTotalMin % 60) + 60) % 60;
  const localStr = `${pad(localH)}:${String(localMin).padStart(2,'0')}:${String(now.getUTCSeconds()).padStart(2,'0')}`;
  const utcH = now.getUTCHours();

  // EST display
  const estH   = Math.floor(estFracHour);
  const estMin = Math.round((estFracHour - estH) * 60);
  const estStr = `${pad(estH)}:${String(estMin).padStart(2,'0')}:${String(now.getUTCSeconds()).padStart(2,'0')}`;

  // Convert EST fractional hour → local fractional hour
  const estToLocal = estH => {
    const utcH = estH - estOffset;
    return utcH + utcOffset;
  };

  function isKzActive(kz) {
    const o = kz.estOpen, c = kz.estClose;
    if (o < c) return estFracHour >= o && estFracHour < c;
    // wraps midnight (e.g. 20:00–00:00)
    return estFracHour >= o || estFracHour < c;
  }

  function minutesLeft(kz) {
    const o = kz.estOpen, c = kz.estClose;
    let closeEst = c;
    if (o >= c && estFracHour >= o) closeEst = c + 24; // past midnight wrap
    return Math.max(0, (closeEst - estFracHour) * 60);
  }

  const zones = KILLZONES[tab].map(kz => {
    const active = isKzActive(kz);
    const mLeft  = active ? minutesLeft(kz) : null;
    const localOpen  = estToLocal(kz.estOpen);
    const localClose = estToLocal(kz.estClose);
    return { ...kz, active, mLeft, localOpen, localClose };
  });

  const activeZone = zones.find(z => z.active);

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:18 }}>

      {/* ── Clock row ── */}
      <div className="kfxt-clock-row" style={{ display:'flex', alignItems:'stretch', gap:12, flexWrap:'wrap' }}>
        {/* Local */}
        <div style={{ background:'rgba(212,168,67,.07)', border:'1px solid rgba(212,168,67,.2)', borderRadius:14, padding:'18px 22px', flex:'1 1 150px', minWidth:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
            <span style={{ fontSize:10, color:'rgba(212,168,67,.55)', letterSpacing:'.1em', textTransform:'uppercase', fontFamily:"'Inter',sans-serif" }}>Your Time</span>
            <span style={{ fontSize:9, padding:'2px 7px', borderRadius:4, background:'rgba(212,168,67,.12)', color:'rgba(212,168,67,.65)', fontFamily:"'Inter',monospace" }}>{offsetStr}</span>
          </div>
          <div style={{ fontSize:30, fontWeight:800, color:'#D4A843', fontFamily:"'Inter',monospace", letterSpacing:'.04em', lineHeight:1 }}>{localStr}</div>
          <div style={{ fontSize:11, color:'rgba(168,159,140,.45)', marginTop:8 }}>📍 {city}</div>
        </div>
        {/* EST */}
        <div style={{ background:'rgba(255,255,255,.025)', border:'1px solid rgba(255,255,255,.07)', borderRadius:14, padding:'18px 20px', flex:'1 1 130px', minWidth:0 }}>
          <div style={{ fontSize:10, color:'rgba(168,159,140,.38)', letterSpacing:'.1em', textTransform:'uppercase', marginBottom:8, fontFamily:"'Inter',sans-serif" }}>EST / New York</div>
          <div style={{ fontSize:24, fontWeight:700, color:'rgba(168,159,140,.7)', fontFamily:"'Inter',monospace", letterSpacing:'.04em', lineHeight:1 }}>{estStr}</div>
          <div style={{ fontSize:10, color:'rgba(168,159,140,.3)', marginTop:8 }}>UTC{estOffset >= 0 ? '+' : ''}{estOffset}</div>
        </div>
        {/* Active killzone badge */}
        {activeZone && (
          <div style={{ padding:'14px 18px', background:`${activeZone.color}10`, border:`1px solid ${activeZone.color}38`, borderRadius:14, display:'flex', flexDirection:'column', justifyContent:'center', gap:4, flex:'1 1 140px', minWidth:0 }}>
            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
              <span style={{ width:6, height:6, borderRadius:'50%', background:activeZone.color, animation:'kfxt-dotPulse 1.5s infinite' }} />
              <span style={{ fontSize:9, color:`${activeZone.color}cc`, letterSpacing:'.1em', textTransform:'uppercase', fontFamily:"'Inter',sans-serif" }}>Killzone Active</span>
            </div>
            <div style={{ fontSize:14, fontWeight:700, color:activeZone.color }}>{activeZone.icon} {activeZone.name}</div>
            <div style={{ fontSize:11, color:'rgba(168,159,140,.45)' }}>
              {activeZone.mLeft != null && `${Math.floor(activeZone.mLeft)}m remaining`}
            </div>
          </div>
        )}
      </div>

      {/* ── Tabs ── */}
      <div style={{ display:'flex', gap:6 }}>
        {[{ key:'forex', label:'Forex Killzones' }, { key:'indices', label:'Indices Killzones' }].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              flex:1, padding:'9px 0', borderRadius:9, border:'none', cursor:'pointer', fontFamily:"'Inter',sans-serif",
              fontSize:12, fontWeight: tab === t.key ? 700 : 500, letterSpacing:'.02em',
              background: tab === t.key ? 'linear-gradient(90deg,rgba(212,168,67,.18),rgba(212,168,67,.08))' : 'rgba(255,255,255,.03)',
              color: tab === t.key ? '#D4A843' : 'rgba(168,159,140,.45)',
              border: `1px solid ${tab === t.key ? 'rgba(212,168,67,.3)' : 'rgba(255,255,255,.07)'}`,
              transition:'all .15s',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Killzone grid ── */}
      <div className="kfxt-main-grid" style={{ display:'grid', gridTemplateColumns:'repeat(2, 1fr)', gap:12 }}>
        {zones.map(z => (
          <div key={z.name} style={{ background: z.active ? `${z.color}0d` : 'rgba(255,255,255,.02)', border:`1px solid ${z.active ? z.color + '40' : 'rgba(255,255,255,.06)'}`, borderRadius:12, padding:'14px 16px', transition:'all .25s' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
              <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                <span style={{ width:8, height:8, borderRadius:'50%', background:z.color, flexShrink:0, opacity: z.active ? 1 : 0.25, boxShadow: z.active ? `0 0 10px ${z.color}` : 'none', animation: z.active ? 'kfxt-pulse 2s infinite' : 'none' }} />
                <span style={{ fontSize:13, fontWeight:700, color: z.active ? '#F7F3EA' : 'rgba(168,159,140,.4)', fontFamily:"'Inter',sans-serif" }}>{z.icon} {z.name}</span>
              </div>
              <span style={{ fontSize:9, fontWeight:700, padding:'3px 7px', borderRadius:5, background: z.active ? `${z.color}22` : 'rgba(255,255,255,.04)', color: z.active ? z.color : 'rgba(168,159,140,.28)', letterSpacing:'.07em', fontFamily:"'Inter',sans-serif" }}>
                {z.active ? 'ACTIVE' : 'CLOSED'}
              </span>
            </div>
            {/* EST time */}
            <div style={{ fontSize:12, fontWeight:700, color: z.active ? 'rgba(245,240,232,.75)' : 'rgba(168,159,140,.45)', fontFamily:"'Inter',monospace", marginBottom:3 }}>
              {fmtH(z.estOpen)}–{fmtH(z.estClose)} EST
            </div>
            {/* Local time conversion */}
            <div style={{ fontSize:10, color:'rgba(168,159,140,.32)', fontFamily:"'Inter',monospace" }}>
              {fmtH(z.localOpen)}–{fmtH(z.localClose)} {offsetStr}
            </div>
            {z.active && z.mLeft != null && (
              <div style={{ marginTop:5, fontSize:11, color:z.color, opacity:.8 }}>
                {Math.floor(z.mLeft)}m {Math.round((z.mLeft % 1) * 60)}s left
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Tip */}
      <div style={{ padding:'12px 16px', background:'rgba(212,168,67,.04)', border:'1px solid rgba(212,168,67,.1)', borderRadius:10, fontSize:12, color:'rgba(168,159,140,.55)', lineHeight:1.7 }}>
        💡 <strong style={{ color:'rgba(212,168,67,.7)' }}>Tip:</strong> All times shown in EST and your local time. Killzones mark ICT's highest-probability windows — price delivers the sharpest moves at session opens and closes.
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   TOOLS REGISTRY
═══════════════════════════════════════════════════════════════════ */
const TOOL_DEFS = [
  { id:'position', icon:'⚖️',  label:'Position Sizing',     sub:'Risk-based lot calculator for any pair', section:'RISK'     },
  { id:'rr',       icon:'🎯',  label:'Risk:Reward',          sub:'R:R ratio with payout',                section:'RISK',   badge:'NEW' },
  { id:'target',   icon:'🏆',  label:'Profit Planner',       sub:'Reverse-engineer your monthly goal',   section:'PLANNING', badge:'NEW' },
  { id:'prop',     icon:'🏛️',  label:'Prop Firm Tracker',    sub:'Challenge progress & limit alerts',    section:'PLANNING', badge:'NEW' },
  { id:'drawdown', icon:'📉',  label:'Drawdown Recovery',    sub:'Monte Carlo recovery simulation',      section:'ANALYSIS' },
  { id:'session',  icon:'🌐',  label:'Session Clock',        sub:'Live forex sessions · auto-timezone',  section:'ANALYSIS', badge:'LIVE' },
];

/* ═══════════════════════════════════════════════════════════════════
   RESPONSIVE HOOK
═══════════════════════════════════════════════════════════════════ */
function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth < breakpoint : false);
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const handler = e => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    setIsMobile(mq.matches);
    return () => mq.removeEventListener('change', handler);
  }, [breakpoint]);
  return isMobile;
}

/* ═══════════════════════════════════════════════════════════════════
   MAIN PAGE EXPORT
═══════════════════════════════════════════════════════════════════ */
export default function ToolsPage({ trades = [], profile = {} }) {
  const [activeId,    setActiveId]    = useState('position');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isMobile  = useIsMobile(640);
  const accountSize = profile?.accountSize || '';

  const tradeStats = useMemo(() => {
    const wins   = trades.filter(t => t.result > 0);
    const losses = trades.filter(t => t.result < 0);
    return {
      winRate: trades.length ? Math.round((wins.length / trades.length) * 100) : null,
      avgWin:  wins.length   ? (wins.reduce((s, t) => s + t.result, 0) / wins.length).toFixed(2) : null,
      avgLoss: losses.length ? (Math.abs(losses.reduce((s, t) => s + t.result, 0) / losses.length)).toFixed(2) : null,
    };
  }, [trades]);

  const activeTool = TOOL_DEFS.find(t => t.id === activeId);
  const sections   = [...new Set(TOOL_DEFS.map(t => t.section))];
  const handleToolSelect = id => { setActiveId(id); setSidebarOpen(false); };

  const renderTool = () => {
    const props = { accountSize, trades, tradeStats, isMobile };
    switch (activeId) {
      case 'position': return <ToolPositionSizing {...props} />;
      case 'rr':       return <ToolRiskReward     {...props} />;
      case 'target':   return <ToolProfitTarget   {...props} />;
      case 'prop':     return <ToolPropFirm       {...props} />;
      case 'drawdown': return <ToolDrawdown       {...props} />;
      case 'session':  return <ToolSessionClock isMobile={isMobile} />;
      default:         return null;
    }
  };

  // Sidebar — shared between desktop + mobile drawer
  const SidebarContent = () => (
    <>
      {/* Journal sync chip */}
      {(accountSize || trades.length > 0) && (
        <div style={{ margin:'0 10px 16px', padding:'9px 12px', background:'rgba(46,204,138,.06)', border:'1px solid rgba(46,204,138,.14)', borderRadius:8, fontSize:10, color:'rgba(46,204,138,.75)', lineHeight:1.5, display:'flex', alignItems:'flex-start', gap:6 }}>
          <span style={{ marginTop:1 }}>✓</span>
          <span>{[accountSize && 'Account balance', trades.length > 0 && 'Trade stats'].filter(Boolean).join(' & ')} pre-filled from journal</span>
        </div>
      )}

      {sections.map(sec => (
        <div key={sec} style={{ marginBottom:4 }}>
          <div style={{ fontSize:9, fontWeight:700, letterSpacing:'.13em', textTransform:'uppercase', color:'rgba(212,168,67,.38)', padding:'8px 18px 5px', fontFamily:"'Inter',sans-serif" }}>
            {sec}
          </div>

          {TOOL_DEFS.filter(t => t.section === sec).map(tool => {
            const isActive = activeId === tool.id;
            return (
              <button
                key={tool.id}
                className={`kfxt-nav-btn${isActive ? ' active' : ''}`}
                onClick={() => handleToolSelect(tool.id)}
              >
                {/* Active left indicator */}
                {isActive && (
                  <span style={{ position:'absolute', left:0, top:'18%', bottom:'18%', width:3, borderRadius:'0 3px 3px 0', background:'linear-gradient(180deg,transparent,#D4A843,transparent)', boxShadow:'0 0 8px rgba(212,168,67,.5)' }} />
                )}

                {/* Icon */}
                <span style={{ width:30, height:30, borderRadius:8, background: isActive ? 'rgba(212,168,67,.14)' : 'rgba(255,255,255,.04)', border:`1px solid ${isActive ? 'rgba(212,168,67,.28)' : 'rgba(255,255,255,.07)'}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, flexShrink:0, transition:'all .15s' }}>
                  {tool.icon}
                </span>

                {/* Label */}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:12, fontWeight: isActive ? 700 : 500, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {tool.label}
                  </div>
                </div>

                {/* Badge */}
                {tool.badge && (
                  <span style={{ fontSize:7, padding:'2px 5px', borderRadius:4, background: tool.badge === 'LIVE' ? 'rgba(46,204,138,.15)' : 'rgba(212,168,67,.12)', color: tool.badge === 'LIVE' ? '#2ECC8A' : '#D4A843', fontFamily:"'Inter',sans-serif", fontWeight:700, letterSpacing:'.06em', flexShrink:0 }}>
                    {tool.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      ))}
    </>
  );

  return (
    <>
      <style>{STYLES}</style>

      <div style={{ display:'flex', flexDirection:'column', height:'100%', minHeight:0, overflow:'hidden' }}>

        {/* ── Main row ─────────────────────────────────────── */}
        <div style={{ display:'flex', flex:1, minHeight:0, overflow:'hidden' }}>

          {/* ── Desktop sidebar ── */}
          {!isMobile && (
            <div style={{ width:216, flexShrink:0, borderRight:'1px solid rgba(255,255,255,.05)', background:'var(--dark)', overflowY:'auto', padding:'18px 0', display:'flex', flexDirection:'column' }}>
              {/* Sidebar header */}
              <div style={{ padding:'0 18px 16px', borderBottom:'1px solid rgba(255,255,255,.04)', marginBottom:12 }}>
                <div style={{ fontSize:9, fontWeight:700, letterSpacing:'.14em', textTransform:'uppercase', color:'rgba(212,168,67,.5)', fontFamily:"'Inter',sans-serif" }}>
                  Trading Tools
                </div>
                <div style={{ fontSize:11, color:'rgba(168,159,140,.35)', marginTop:3, fontFamily:"'Inter',sans-serif" }}>
                  6 professional calculators
                </div>
              </div>
              <SidebarContent />
            </div>
          )}

          {/* ── Mobile drawer overlay ── */}
          {isMobile && sidebarOpen && (
            <>
              <div onClick={() => setSidebarOpen(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.65)', zIndex:99, backdropFilter:'blur(4px)' }} />
              <div style={{ position:'fixed', top:0, left:0, bottom:0, width:275, zIndex:100, background:'var(--dark)', borderRight:'1px solid rgba(255,255,255,.09)', overflowY:'auto', padding:'0 0 20px', display:'flex', flexDirection:'column', animation:'kfxt-slideIn .2s ease both', boxShadow:'16px 0 48px rgba(0,0,0,.7)' }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 14px 12px', borderBottom:'1px solid rgba(255,255,255,.06)', marginBottom:8 }}>
                  <span style={{ fontSize:10, fontWeight:700, color:'rgba(212,168,67,.65)', letterSpacing:'.1em', textTransform:'uppercase' }}>Trading Tools</span>
                  <button onClick={() => setSidebarOpen(false)} style={{ background:'rgba(255,255,255,.06)', border:'1px solid rgba(255,255,255,.1)', color:'rgba(168,159,140,.6)', borderRadius:6, padding:'4px 10px', cursor:'pointer', fontSize:15, lineHeight:1 }}>✕</button>
                </div>
                <SidebarContent />
              </div>
            </>
          )}

          {/* ── Content area ── */}
          <div className="kfxt-scroll" style={{ flex:1, overflowY:'auto', minWidth:0, padding: isMobile ? '16px 14px 32px' : '28px 32px 56px' }}>

            {/* ── Tool header ── */}
            <div style={{ marginBottom: isMobile ? 18 : 26, display:'flex', alignItems:'flex-start', gap: isMobile ? 10 : 14 }}>
              {isMobile && (
                <button onClick={() => setSidebarOpen(true)} style={{ width:38, height:38, borderRadius:9, background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.09)', color:'rgba(168,159,140,.6)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0, marginTop:2 }} aria-label="Open tools menu">
                  ☰
                </button>
              )}

              {/* Tool icon */}
              <div style={{ width: isMobile ? 38 : 46, height: isMobile ? 38 : 46, borderRadius:12, background:'linear-gradient(135deg,rgba(212,168,67,.16),rgba(212,168,67,.04))', border:'1px solid rgba(212,168,67,.22)', display:'flex', alignItems:'center', justifyContent:'center', fontSize: isMobile ? 18 : 21, flexShrink:0 }}>
                {activeTool?.icon}
              </div>

              {/* Title group */}
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:9, fontWeight:700, color:'rgba(212,168,67,.55)', letterSpacing:'.13em', textTransform:'uppercase', fontFamily:"'Inter',sans-serif", marginBottom:4 }}>
                  Trading Tool
                </div>
                <h1 style={{ fontSize: isMobile ? 19 : 24, fontWeight:800, color:'var(--white)', margin:0, letterSpacing:'-.025em', lineHeight:1.1, fontFamily:"'Inter',sans-serif" }}>
                  {activeTool?.label}
                </h1>
                <p style={{ fontSize:12, color:'rgba(168,159,140,.45)', margin:'4px 0 0', fontFamily:"'Inter',sans-serif", lineHeight:1.4 }}>
                  {activeTool?.sub}
                </p>
              </div>

              {/* Auto-save indicator */}
              {!isMobile && (
                <div style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 12px', background:'rgba(255,255,255,.03)', border:'1px solid rgba(255,255,255,.06)', borderRadius:8, flexShrink:0, alignSelf:'flex-start', marginTop:4 }}>
                  <span style={{ width:5, height:5, borderRadius:'50%', background:'#2ECC8A', animation:'kfxt-dotPulse 2s infinite' }} />
                  <span style={{ fontSize:10, color:'rgba(168,159,140,.5)', fontFamily:"'Inter',sans-serif" }}>Auto-calculating</span>
                </div>
              )}
            </div>

            {/* ── Tool panel ── */}
            <div className="kfxt-tool-panel">
              {renderTool()}
            </div>

            {/* ── Cross-tool tip (non-session) ── */}
            {activeId !== 'session' && (
              <div style={{ marginTop:20, padding:'12px 16px', background:'rgba(255,255,255,.02)', border:'1px solid rgba(255,255,255,.04)', borderRadius:10, fontSize:12, color:'rgba(168,159,140,.4)', lineHeight:1.7 }}>
                💡 <strong style={{ color:'rgba(212,168,67,.6)' }}>Use together:</strong> Calculate your position size first, verify the R:R, then check if the session clock shows an overlap — that's the complete pre-trade checklist.
              </div>
            )}
          </div>
        </div>

        {/* ── Mobile bottom tab bar ── */}
        {isMobile && (
          <div className="kfxt-tab-bar" style={{ display:'flex', flexShrink:0, background:'var(--dark)', borderTop:'1px solid rgba(255,255,255,.08)', overflowX:'auto', gap:0, paddingBottom:'env(safe-area-inset-bottom)', scrollbarWidth:'none', WebkitOverflowScrolling:'touch' }}>
            {TOOL_DEFS.map(tool => (
              <button
                key={tool.id}
                className={`kfxt-tab-btn${activeId === tool.id ? ' active' : ''}`}
                onClick={() => setActiveId(tool.id)}
              >
                <span style={{ fontSize:19, lineHeight:1 }}>{tool.icon}</span>
                <span style={{ fontSize:9, fontWeight: activeId === tool.id ? 700 : 400, whiteSpace:'nowrap', letterSpacing:'.01em', lineHeight:1.2 }}>
                  {tool.label.split(' ')[0]}
                </span>
              </button>
            ))}
          </div>
        )}

      </div>
    </>
  );
}